import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useGetUserByIdQuery, useDeleteEmployeeWithDependanciesMutation } from "../../../api/userApi";
import { EmployeeDetailsPresentational } from "./employeeDetails";
import { LoadingIndicator } from "../../shared/alerts/loadingIndicator";
import { QrCodePopup } from "../../shared/qrCode/qrPopup";
import { ConfirmationDialog } from "../../shared/confirmationDialog/confirmationDialog";
import { useToast } from "../../../hooks/useToast";
import { Collection } from "../../../enums/collections.enum";
import { useAuthenticatedUser } from "../../../hooks/useAuthenticatedUser";
import { useLoader } from "../../../hooks/useLoader";
import { useGetCompanyByIdQuery } from "../../../api/companyApi";

export const EmployeeDetailsContainer: React.FC = () => {
  const { userId, companyId } = useParams<{ userId?: string; companyId?: string }>();

  const { data: employee, isLoading: loading } = useGetUserByIdQuery(userId ?? '');
  const { data: company } = useGetCompanyByIdQuery(companyId ?? "");
  const { user } = useAuthenticatedUser();
  const isEmployeeOwner = employee?.ownerId === user?.id;

  const [qrPopupOpen, setQrPopupOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteEmployee] = useDeleteEmployeeWithDependanciesMutation();
  const { showSuccess, showError } = useToast();
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();

  // Handle QR code click
  const handleQrCodeClick = () => {
    setQrPopupOpen(true);
  };

  // Close QR popup
  const handleQrPopupClose = () => {
    setQrPopupOpen(false);
  };

  // Open delete confirmation dialog
  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    setDeleteDialogOpen(false);

    if(userId) {
    try {
      showLoader("Deleting employee, image and reviews...");
      await deleteEmployee({ userId }).unwrap();
      hideLoader();
      showSuccess("Employee deleted successfully");
      // Navigate back to the branch page
      navigate(`/${Collection.Companies}/${companyId ?? ""}/${Collection.Branches}/${employee?.branchId}`);
    } catch (error: any) {
      hideLoader();
      showError(error?.message || "Failed to delete employee");
    }
  }else {
    showError("User id not found");
  }
  };

  // Cancel delete
  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  // Handle loading and error states
  if (loading) return <LoadingIndicator message="Loading employee data..." />;
  if (!employee) return <p>Employee not found</p>;

  return (
    <>
      <EmployeeDetailsPresentational
        userData={employee}
        onQrCodeClick={handleQrCodeClick}
        onDeleteClick={handleDeleteClick}
        isEmployeeOwner={isEmployeeOwner}
      />
      
      <QrCodePopup
        open={qrPopupOpen}
        onClose={handleQrPopupClose}
        qrValue={window.location.href}
        imageUrl={employee.image}
        companyName={company?.name}
      />

      <ConfirmationDialog
        open={deleteDialogOpen}
        title="Delete Employee"
        message={`Are you sure you want to delete ${employee.firstName} ${employee.lastName}? This will delete all their reviews and images as well.`}
        confirmButtonText="Delete"
        confirmButtonColor="error"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </>
  );
};

